<template>
  <IntercomModal
    v-model:show="showModal"
    :title="$t('payments.recordPayment')"
    width="600px"
  >
    <!-- Invoice Details -->
    <div v-if="invoice" class="invoice-details">
      <div class="invoice-detail-row">
        <span class="label">{{ $t('invoices.invoiceNumber') }}:</span>
        <span class="value">{{ invoice.invoice_number }}</span>
      </div>
      <div class="invoice-detail-row">
        <span class="label">{{ $t('invoices.customer') }}:</span>
        <span class="value">{{ invoice.customer_name }}</span>
      </div>
      <div class="invoice-detail-row">
        <span class="label">{{ $t('invoices.grandTotal') }}:</span>
        <span class="value">₹{{ invoice.total_amount?.toFixed(2) }}</span>
      </div>
      <div class="invoice-detail-row">
        <span class="label">{{ $t('payments.totalPaid') }}:</span>
        <span class="value">₹{{ invoice.amount_paid?.toFixed(2) || '0.00' }}</span>
      </div>
      <div class="invoice-detail-row highlight">
        <span class="label">{{ $t('payments.amountDue') }}:</span>
        <span class="value">₹{{ amountDue.toFixed(2) }}</span>
      </div>
    </div>

    <div class="payment-form">
      <IntercomInput
        v-model.number="formData.amount"
        type="number"
        :label="$t('payments.amount')"
        :placeholder="$t('payments.amount')"
        required
        :error="errors.amount"
      />

      <IntercomSelect
        v-model="formData.payment_mode"
        :label="$t('payments.paymentMode') + ' *'"
        :options="paymentModeOptions"
      />

      <IntercomDateInput
        v-model="formData.payment_date"
        :label="$t('payments.paymentDate') + ' *'"
        required
      />

      <IntercomInput
        v-model="formData.reference_number"
        :label="$t('payments.referenceNumber')"
        :placeholder="$t('payments.referenceNumber')"
      />

      <IntercomInput
        v-model="formData.notes"
        type="textarea"
        :label="$t('payments.notes')"
        :placeholder="$t('payments.notes')"
        :rows="3"
      />
    </div>

    <template #footer>
      <IntercomButton variant="secondary" @click="handleCancel">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton variant="primary" :loading="isLoading" @click="handleSubmit">
        {{ $t('common.save') }}
      </IntercomButton>
    </template>
  </IntercomModal>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { IntercomModal, IntercomInput, IntercomDateInput, IntercomButton, IntercomSelect } from '@shared/components/intercom'
import { useBilling } from '../composables/useBilling.js'

const props = defineProps({
  show: Boolean,
  invoice: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['update:show', 'success'])

const { t } = useI18n()
const { createPayment } = useBilling()

// State
const showModal = ref(props.show)
const isLoading = ref(false)

// Computed amount due
const amountDue = computed(() => {
  if (!props.invoice) return 0
  return (props.invoice.total_amount || 0) - (props.invoice.amount_paid || 0)
})

// Payment mode options
const paymentModeOptions = computed(() => [
  { label: t('payments.modes.cash'), value: 'cash' },
  { label: t('payments.modes.card'), value: 'card' },
  { label: t('payments.modes.upi'), value: 'upi' },
  { label: t('payments.modes.cheque'), value: 'cheque' },
  { label: t('payments.modes.bank_transfer'), value: 'bank_transfer' },
])

// Form data
const formData = reactive({
  amount: null,
  payment_mode: 'cash',
  payment_date: '',
  reference_number: '',
  notes: '',
})

// Form errors
const errors = reactive({
  amount: '',
})

// Watch show prop
watch(
  () => props.show,
  (val) => {
    showModal.value = val
    if (val && props.invoice) {
      // Populate form with defaults
      formData.amount = amountDue.value
      formData.payment_mode = 'cash'
      formData.payment_date = new Date().toISOString().split('T')[0]
      formData.reference_number = ''
      formData.notes = ''
    } else {
      // Reset form
      resetForm()
    }
    // Clear errors when opening
    clearErrors()
  }
)

// Watch modal state
watch(showModal, (val) => {
  emit('update:show', val)
})

/**
 * Clear errors
 */
const clearErrors = () => {
  errors.amount = ''
}

/**
 * Validate form
 */
const validateForm = () => {
  clearErrors()
  let isValid = true

  // Validate amount
  if (!formData.amount || formData.amount <= 0) {
    errors.amount = 'Amount must be greater than 0'
    isValid = false
  } else if (formData.amount > amountDue.value) {
    errors.amount = `Amount cannot exceed ₹${amountDue.value.toFixed(2)}`
    isValid = false
  }

  return isValid
}

/**
 * Reset form
 */
const resetForm = () => {
  Object.assign(formData, {
    amount: null,
    payment_mode: 'cash',
    payment_date: '',
    reference_number: '',
    notes: '',
  })
  clearErrors()
}

/**
 * Handle cancel
 */
const handleCancel = () => {
  showModal.value = false
  resetForm()
}

/**
 * Handle submit
 */
const handleSubmit = async () => {
  // Validate form
  if (!validateForm()) {
    return
  }

  isLoading.value = true

  try {
    // Prepare data
    const paymentData = {
      invoice_id: props.invoice.id,
      amount: formData.amount,
      payment_mode: formData.payment_mode,
      // Backend expects RFC 3339 time.Time; date picker gives "YYYY-MM-DD"
      payment_date: formData.payment_date ? new Date(formData.payment_date).toISOString() : null,
      reference_number: formData.reference_number || null,
      notes: formData.notes || null,
    }

    // Create payment
    await createPayment(paymentData)
    console.log(t('payments.createSuccess'))

    // Close modal and emit success
    showModal.value = false
    resetForm()
    emit('success')
  } catch (error) {
    console.error('Failed to record payment:', error)
    // TODO: Show error notification
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.invoice-details {
  margin-bottom: var(--space-6);
  padding: var(--space-4);
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-gray-200);
}

.invoice-detail-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-2);
}

.invoice-detail-row:last-child {
  margin-bottom: 0;
}

.invoice-detail-row .label {
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  font-size: var(--font-size-sm);
}

.invoice-detail-row .value {
  color: var(--color-charcoal);
  font-size: var(--font-size-sm);
}

.invoice-detail-row.highlight .label,
.invoice-detail-row.highlight .value {
  color: #d03050;
  font-weight: var(--font-weight-semibold);
}

.payment-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.form-date-input {
  width: 100%;
  height: 44px;
  padding: 0 var(--space-3);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  outline: none;
  transition: all var(--transition-fast);
}

.form-date-input:focus {
  background: var(--color-white);
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1);
}
</style>
